// /machines: my Share-hosted machine (RFC-050 model 3, byj-1wv.62.8) —
// start it, open it (its terminal share, which I type into, or its
// workbench, RFC-057), stop it — and
// how long my machines have run this month. Machines in Share's cloud are by
// grant during the beta (RFC-050 "By grant", byj-1wv.62.17): with none, or
// one ended, there is no Start but what to ask for. A machine started for an
// exam (byj-1wv.62.10) says which, and that its home is the exam's. Below,
// always, a machine on my own computer (byj-1wv.62.23, .25): the published
// image's docker run for this Share, which needs no grant, and those of mine
// running now, each to open. Laid out by what each part is for
// (byj-1wv.51.15): the machine and what to do with it beside the month's use,
// how to use one under both, and my own computer's machines as rows with how
// to run one under them; as wide as /shares.
import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type MachinesView, type OwnMachines as OwnMachinesView } from "../api";
import { pollMs, pollStartingMs } from "../machines";
import { useConfirm } from "../components/Confirm";
import { Meter, nearlyFull } from "../components/Meter";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLead } from "../components/Page";
import { EmptyRows, Grouped, initialOf } from "../components/Rows";
import { FoldPanel } from "../components/SidePanel";
import { Steps, Tips } from "../components/Steps";
import { useKeptChoice } from "../kept";
import { navigate } from "../router";
import { phrases, t, whenMinute } from "../i18n";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** The command that runs a machine on one's own computer, against this Share. */
export function ownMachineCommand(o: Pick<OwnMachinesView, "image" | "relay">): string {
  return `docker run -it --rm --platform linux/amd64 -v byj-home:/home/dev -e BYJ_SHARE_RELAY=${o.relay} ${o.image}`;
}

/** When the month's hours start again: the relay counts a month in UTC (RFC-050, `machine_usage`). */
export function nextMonth(now: Date = new Date()): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)).toISOString();
}

type OwnMachine = OwnMachinesView["machines"][number];

/** One of mine running on my own computer, as /shares' rows: its title to open, when it started. */
function OwnMachineRow({ m }: { m: OwnMachine }) {
  const to = `/s/${encodeURIComponent(m.share_id)}`;
  return (
    <li className="share-row own-machine-row is-live no-menu">
      <span className="row-lead">
        <span className="state-dot" aria-hidden="true" />
        <span className="sr-only">{t("shares:live")}</span>
      </span>
      <span className="kind-box" aria-hidden="true">
        {initialOf(m.title)}
      </span>
      <div className="row-main">
        <Link to={to} className="row-title">
          {m.title}
        </Link>
        <div className="row-sub">{t("machines:machineStarted", { w: whenMinute(m.started_at) })}</div>
      </div>
      <div className="row-meta">
        <button className="small" title={t("workbench:openWorkbenchTitle")} onClick={() => navigate(`${to}/workbench`)}>
          {t("workbench:openWorkbench")}
        </button>
        <button className="primary small" onClick={() => navigate(to)}>
          {t("machines:machineOpen")}
        </button>
      </div>
    </li>
  );
}

/** A machine on my own computer: how to run one, step by step, and mine running now under it. */
function OwnMachines({ load }: { load: typeof api.ownMachines }) {
  const [data, setData] = useState<OwnMachinesView | null>(null);
  const [error, setError] = useState<string | null>(null);
  // How to run one is open while none runs, as the page first finds them:
  // decided once, so a machine starting or stopping under the reader leaves
  // it as they have it
  const [howOpen, setHowOpen] = useState<boolean | null>(null);
  const fetch = useCallback(async () => {
    try {
      const d = await load();
      setData(d);
      // decided with the first answer, in the render that draws it: never a
      // render of mine running with How to run one still open, folded only by
      // an effect after it (byj-1wv.103)
      setHowOpen((was) => was ?? d.machines.length === 0);
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }, [load]);
  useEffect(() => {
    void fetch();
    const timer = setInterval(() => void fetch(), pollMs);
    return () => clearInterval(timer);
  }, [fetch]);
  return (
    <>
      <h2 id="own-machines">{t("machines:machinesOwnTitle")}</h2>
      <p className="muted section-lead">{t("machines:machinesOwnLead")}</p>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      <section aria-labelledby="own-machines" className="stack machine-own" data-testid="own-machines">
        {/* mine running first, what is there to open; how to run one under it,
            open while none runs and folded once one does, as it was read */}
        {data && (
          <div className="rows-panel">
            <Grouped
              id="own-machines"
              headed
              groups={[{ title: t("machines:machinesOwnRunning"), items: data.machines, none: <EmptyRows text={t("machines:machinesOwnNone")} />, testid: "own-machine-list" }]}
              row={(m) => <OwnMachineRow key={m.share_id} m={m} />}
            />
          </div>
        )}
        <FoldPanel
          key={howOpen === null ? "loading" : "found"}
          className="card pad machine-fold"
          label={t("machines:machinesOwnHow")}
          summary={<span className="muted small">{t("machines:machinesOwnHowBrief")}</span>}
          open={howOpen ?? true}
          testid="own-machine-steps"
        >
          <Steps
            rail
            testid="own-machine-command"
            steps={phrases.machineOwnSteps(data ? ownMachineCommand(data) : undefined)}
          />
        </FoldPanel>
      </section>
    </>
  );
}

/** The month's use as a tile, as Admin's file storage is: the hours used large, of the cap, a bar of them, and when the grant ends. */
function MonthUse({ data }: { data: MachinesView }) {
  const used = data.month_cap_seconds > 0 ? data.month_seconds / data.month_cap_seconds : 1;
  const month = t("machines:machinesMonth", { used: phrases.machineHours(data.month_seconds), cap: phrases.machineHours(data.month_cap_seconds) });
  return (
    <section className="storage-tile machine-month" aria-label={t("machines:machinesMonthTitle")} data-testid="machine-month">
      <span className="muted small">{t("machines:machinesMonthTitle")}</span>
      <strong className={used >= 1 ? "error" : ""}>{phrases.machineHours(data.month_seconds)}</strong>
      <span className="muted small">{t("machines:machinesMonthOf", { cap: phrases.machineHours(data.month_cap_seconds) })}</span>
      <Meter label={month} used={used} full={used >= nearlyFull} />
      {data.grant_expires_at && <span className="muted small">{t("machines:machinesGrantEnds", { w: whenMinute(data.grant_expires_at) })}</span>}
    </section>
  );
}

/**
 * When their home goes, as the relay works it out — it says a day only while
 * no machine runs on it (byj-1wv.51.15) — and what keeps it: a machine
 * started before then, or, with no Start to offer (a grant ended), the work
 * pushed somewhere else.
 */
function HomeRemoves({ at, canStart }: { at?: string; canStart: boolean }) {
  if (!at) return null;
  return (
    <p className="small flush warn" data-testid="machine-home-removes">
      {canStart ? t("machines:machineHomeRemovesAt", { w: whenMinute(at) }) : t("machines:machineHomeRemovesAtNoStart", { w: whenMinute(at) })}
    </p>
  );
}

/** Where the page keeps whether Inside a machine is open or folded, per browser. */
const InsideKey = "machines.inside";

/**
 * Inside a machine, across the page under the machine and its month, folded
 * as How to run one is and its steps down the same rail: open it, sign in to
 * the agents used (Claude Code, Codex, each a choice with its command to
 * copy), get to work; then what is good to know (the terminal's selection,
 * the idle stop, how long its home is kept: an exam machine's home its own,
 * said in place of mine). Open at first; folded or opened, kept so per
 * browser, since one signed in has no more need of it whether a machine runs
 * or not.
 */
function Inside({ data, running, exam }: { data: MachinesView; running: boolean; exam?: string }) {
  const [shown, setShown] = useKeptChoice(InsideKey, "open", ["open", "folded"] as const);
  return (
    <FoldPanel
      className="card pad machine-inside machine-fold"
      label={t("machines:machinesInsideTitle")}
      summary={<span className="muted small">{t("machines:machinesInsideBrief")}</span>}
      open={shown === "open"}
      onToggle={(open) => setShown(open ? "open" : "folded")}
      testid="machine-inside"
    >
      <div className="stack">
        <p className="muted small flush">{t("machines:machinesInsideLead")}</p>
        <Steps rail testid="machine-logins" steps={phrases.machineInsideSteps(running, exam !== undefined)} />
        <Tips title={t("machines:machinesTipsTitle")}>
          <li>{t("machines:machineTerminalTip")}</li>
          <li>{t("machines:machineIdleNote")}</li>
          {exam ? (
            <li data-testid="machine-exam-home">{t("machines:machineExamHome")}</li>
          ) : (
            data.home_retain_days !== undefined && <li data-testid="machine-home">{phrases.machineHomeKept(data.home_retain_days)}</li>
          )}
        </Tips>
      </div>
    </FoldPanel>
  );
}

export function Machines({
  load = api.machines,
  start = api.startMachine,
  stop = api.stopMachine,
  own = api.ownMachines,
}: {
  load?: typeof api.machines;
  start?: typeof api.startMachine;
  stop?: typeof api.stopMachine;
  own?: typeof api.ownMachines;
}) {
  const [data, setData] = useState<MachinesView | null>(null);
  const [off, setOff] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ask, confirmDialog] = useConfirm();

  const fetch = useCallback(async () => {
    try {
      setData(await load());
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setOff(true);
      else setError(errText(err));
    }
  }, [load]);
  useEffect(() => {
    void fetch();
  }, [fetch]);

  const machine = data?.machines.find((m) => m.state !== "stopped");
  const waiting = machine !== undefined && !machine.share_id;
  useEffect(() => {
    if (off) return;
    const timer = setInterval(() => void fetch(), waiting ? pollStartingMs : pollMs);
    return () => clearInterval(timer);
  }, [fetch, off, waiting]);

  const act = async (f: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await f();
      setError(null);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
      await fetch();
    }
  };
  const stopIt = async (id: string, exam?: string) => {
    if (await ask(exam ? t("machines:machineStopAskExam") : t("machines:machineStopAsk"), { action: t("machines:machineStop"), danger: true })) await act(() => stop(id));
  };
  const usedUp = data !== null && data.month_seconds >= data.month_cap_seconds;

  return (
    <Page className="machines-page">
      <PageHead title={t("machines:machinesTitle")} />
      <PageLead>{t("machines:machinesLead")}</PageLead>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      <h2>{t("machines:machinesCloudTitle")}</h2>
      {off ? (
        <section className="card pad stack" data-testid="machines-off">
          <p className="flush">{t("machines:machinesOff")}</p>
        </section>
      ) : data === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : data.allowed === false && !machine ? (
        <section className="card pad stack" data-testid="machines-invite">
          <p className="flush">{data.not_allowed === "expired" ? t("machines:machinesGrantEnded") : t("machines:machinesByInvite")}</p>
          <HomeRemoves at={data.home_removes_at} canStart={false} />
        </section>
      ) : (
        <div className="machines-grid">
          {machine ? (
            <section className="card pad stack machine-status" data-testid="machine">
              <div className="row wrap">
                <strong className="grow machine-id">{machine.id}</strong>
                <span className={waiting ? "chip" : "chip chip-ok"}>{waiting ? t("machines:machineStarting") : t("machines:machineRunning")}</span>
              </div>
              {/* each time kept whole on its line, as a row's sub is separated */}
              <div className="muted small machine-times" data-testid="machine-times">
                <span>{t("machines:machineStarted", { w: whenMinute(machine.started_at) })}</span> · <span>{t("machines:machineStopsBy", { w: whenMinute(machine.stops_at) })}</span>
              </div>
              {machine.exam && (
                <p className="small flush" data-testid="machine-exam">
                  {t("machines:machineForExam")} <Link to={`/ea/${encodeURIComponent(machine.exam)}`}>{machine.exam_title || machine.exam}</Link>
                </p>
              )}
              {waiting && <p className="muted small flush">{t("machines:machineStartingNote")}</p>}
              <div className="row wrap machine-actions">
                <button className="primary" disabled={waiting} onClick={() => machine.share_id && navigate(`/s/${encodeURIComponent(machine.share_id)}`)}>
                  {t("machines:machineOpen")}
                </button>
                {/* its files, an editor and its agents in the browser (RFC-057) */}
                <button disabled={waiting} title={t("workbench:openWorkbenchTitle")} onClick={() => machine.share_id && navigate(`/s/${encodeURIComponent(machine.share_id)}/workbench`)}>
                  {t("workbench:openWorkbench")}
                </button>
                <button disabled={busy} onClick={() => void stopIt(machine.id, machine.exam)}>
                  {t("machines:machineStop")}
                </button>
              </div>
            </section>
          ) : (
            <section className="card pad stack machine-status" data-testid="no-machine">
              <p className="flush">{t("machines:noMachine")}</p>
              <div className="row wrap machine-actions">
                <button
                  className="primary"
                  disabled={busy || usedUp}
                  aria-describedby={usedUp ? "machine-used-up" : undefined}
                  onClick={() => void act(start)}
                >
                  {t("machines:machineStart")}
                </button>
                {usedUp && (
                  <span id="machine-used-up" className="small error" data-testid="machine-used-up">
                    {t("machines:machinesMonthUsedUp", { w: whenMinute(nextMonth()) })}
                  </span>
                )}
              </div>
              <HomeRemoves at={data.home_removes_at} canStart />
            </section>
          )}
          <MonthUse data={data} />
          <Inside data={data} running={machine !== undefined} exam={machine?.exam} />
        </div>
      )}
      <OwnMachines load={own} />
      {confirmDialog}
    </Page>
  );
}
